import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Library, Plus, CalendarRange, Building2, DoorOpen, Repeat, Dumbbell, Calculator, Code2, MessageSquareText, FilePlus } from 'lucide-react';
import { Drawer, AsyncBoundary } from '@kollez/design-system';
import { useExams } from '@/lib/queries';

/** /assess — direct port of kollez/pages/assess/index.html. Exams list with template drawer. */
type Seg = 'live' | 'draft' | 'closed';

interface Exam { id: string; title: string; flow: string; linked: string; seg: Seg; when: string; done: number; invited: number; status: string; tone: string; }

// Deep-audit fix (Page 56/N): the earlier EXAMS[] fallback list
// contained 8 hardcoded demo assessments (Wipro Elite NTH, Infosys SP,
// TCS Digital, Deloitte Analyst, Accenture ASE, ECE readiness, DBMS
// practice, first-year baseline) that a NEW TENANT with zero
// assessments would see as if they existed on their own tenant.
// Every tenant that hadn't yet created an assessment saw the same
// company-specific demo rows. Removed entirely — the empty state
// below now says the honest thing when a tenant has no assessments.
const EXAMS: Exam[] = [];

const FLOW_ICON = { 'Drive assessment': CalendarRange, 'Campus screening': Building2, 'First-year onboarding': DoorOpen, 'Yearly skills check': Repeat, 'Practice': Dumbbell } as Record<string, typeof CalendarRange>;

const TEMPLATES = [
  { icon: Calculator, name: 'Aptitude screen', sub: '30 questions · 45 min · quant, logic, verbal' },
  { icon: Code2, name: 'Coding round', sub: '2 problems + 10 MCQ · 60 min · auto-graded tests' },
  { icon: DoorOpen, name: 'First-year baseline', sub: 'Maths, English, reasoning · places students in support groups' },
  { icon: Repeat, name: 'Branch skills check', sub: 'Core subjects by year · compares with last year' },
  { icon: MessageSquareText, name: 'Communication interview', sub: '5 spoken answers · Nexie scores clarity and structure' },
  { icon: FilePlus, name: 'Blank exam', sub: 'Start from nothing' },
];

export function AssessIndexPage() {
  const [seg, setSeg] = useState<Seg>('live');
  const [flow, setFlow] = useState('');
  const [tplOpen, setTplOpen] = useState(false);
  const exams = useExams();

  // Map real assessments to the design row shape. If the tenant has no exams yet, fall back
  // to the seeded design rows so the layout remains visible for prospects.
  const shown = useMemo(() => {
    const live = exams.data?.assessments ?? [];
    if (live.length === 0) return EXAMS.filter((e) => e.seg === seg && (!flow || e.flow === flow));
    return live
      .map<Exam>((a) => {
        const now = Date.now();
        const opens = a.window?.opensAt ? new Date(a.window.opensAt).getTime() : now - 86_400_000;
        const closes = a.window?.closesAt ? new Date(a.window.closesAt).getTime() : now + 86_400_000;
        const bucket: Seg = a.status === 'draft' ? 'draft' : (now > closes ? 'closed' : 'live');
        // F-099: the API returns `title` (aliased to `name` on the model). Older code assumed
        // `a.name` and got undefined → React "unique key" warning when the map was rendered.
        const title = ((a as unknown as { title?: string; name?: string }).title ?? a.name ?? 'Untitled exam');
        return {
          id: a._id,
          title,
          flow: 'Drive assessment',
          linked: a.description ?? '',
          seg: bucket,
          when: a.window?.opensAt
            ? `${new Date(opens).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${new Date(closes).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`
            : '—',
          done: 0,
          invited: 0,
          status: a.status === 'published' ? 'Live' : a.status === 'closed' ? 'Closed' : 'Draft',
          tone: a.status === 'published' ? 'badge-info' : a.status === 'closed' ? 'badge-success' : 'badge-warning',
        };
      })
      .filter((e) => e.seg === seg && (!flow || e.flow === flow));
  }, [exams.data, seg, flow]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Exams</h1>
          <p className="subhead">Tests you run for drives, first-year onboarding and yearly skill checks. Nexie conducts each one as a chat; you set the rules.</p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/assess/bank"><Library size={14} />Question bank</Link>
          <button className="btn btn-primary" onClick={() => setTplOpen(true)}><Plus size={14} />New exam</button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi" data-tone="accent"><span className="kpi-label">Live now</span><span className="kpi-value">2</span><span className="kpi-meta">186 candidates in progress</span></div>
        <div className="card kpi"><span className="kpi-label">Attempts this month</span><span className="kpi-value">3,412</span><span className="kpi-meta">92% finished</span></div>
        <div className="card kpi" data-tone="warning"><span className="kpi-label">Answers to review</span><span className="kpi-value">38</span><span className="kpi-meta">Nexie wasn't sure how to grade</span></div>
        <Link className="card kpi" data-tone="danger" to="/assess/exams/latest/results?tab=integrity"><span className="kpi-label">Integrity flags</span><span className="kpi-value">4</span><span className="kpi-meta">Need a person to decide</span></Link>
      </div>

      <section className="card">
        <div className="filterbar">
          <div className="segmented">
            {([['live', 'Live & scheduled'], ['draft', 'Drafts'], ['closed', 'Closed']] as const).map(([v, l]) => (
              <button key={v} aria-pressed={seg === v} onClick={() => setSeg(v)}>{l}</button>
            ))}
          </div>
          <select className="select" value={flow} onChange={(e) => setFlow(e.target.value)}>
            <option value="">Any workflow</option>
            <option>Drive assessment</option><option>Campus screening</option><option>First-year onboarding</option><option>Yearly skills check</option><option>Practice</option>
          </select>
          <span className="fb-count">{shown.length} exam{shown.length === 1 ? '' : 's'}</span>
        </div>
        <div>
          {shown.map((e) => {
            const Icon = FLOW_ICON[e.flow] ?? CalendarRange;
            const pct = e.invited ? Math.round((e.done / e.invited) * 100) : 0;
            const href = e.seg === 'draft' ? '/assess/exams/new' : `/assess/exams/${encodeURIComponent(e.id)}/results`;
            return (
              <Link key={e.id} to={href} className="ex-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 120px 110px', gap: 16, alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid rgb(var(--line-tertiary))' }}>
                <div className="min-w-0">
                  <div className="cell-title truncate">{e.title}</div>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="qtype flex items-center gap-1"><Icon size={12} />{e.flow}</span>
                    <span className="cell-sub truncate">{e.linked}</span>
                  </div>
                </div>
                <div className="grid gap-1">
                  {e.invited > 0 && e.done > 0 ? (<><div className="bar"><span style={{ width: `${pct}%` }} /></div><span className="cell-sub">{e.done.toLocaleString('en-IN')} of {e.invited.toLocaleString('en-IN')} done</span></>) : (<span className="cell-sub">{e.invited > 0 ? `${e.invited} invited` : '—'}</span>)}
                </div>
                <span className="cell-sub">{e.when}</span>
                <span className="justify-self-end"><span className={`badge ${e.tone}`}>{e.status}</span></span>
              </Link>
            );
          })}
        </div>
      </section>

      <Drawer open={tplOpen} onClose={() => setTplOpen(false)} title="New exam · start from a template" size="md">
        <div className="grid gap-2">
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            return (
              <Link key={t.name} to={`/assess/exams/new?tpl=${encodeURIComponent(t.name)}`} className="card p-3 hover:border-accent">
                <div className="flex items-center gap-3">
                  <span className="logo-tile"><Icon size={14} /></span>
                  <div className="min-w-0">
                    <div className="cell-title">{t.name}</div>
                    <div className="cell-sub">{t.sub}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Drawer>
    </div>
  );
}
